const BOLD_WORDS = ['žlutým', 'růžovým', 'Bílé', 'Žluté', 'Růžové'];

const BOLD_PATTERN = new RegExp(
    `(${BOLD_WORDS.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'g',
);

const DROPDOWN_VIEWPORT_GAP = 24;

function wrapBoldWords(text: string): string {
    const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return safe.replace(BOLD_PATTERN, '<strong>$1</strong>');
}

function applyBoldToNiceSelect(niceSelectEl: Element): void {
    niceSelectEl.querySelectorAll<HTMLLIElement>('.list .option').forEach((li) => {
        const text = li.textContent ?? '';
        li.innerHTML = wrapBoldWords(text);
    });

    const current = niceSelectEl.querySelector<HTMLSpanElement>('.current');
    if (current) {
        const text = current.textContent ?? '';
        current.innerHTML = wrapBoldWords(text);
    }
}

function observeCurrentSpan(niceSelectEl: Element): void {
    const current = niceSelectEl.querySelector<HTMLSpanElement>('.current');
    if (!current) {
        return;
    }

    const observer = new MutationObserver(() => {
        observer.disconnect();
        const text = current.textContent ?? '';
        current.innerHTML = wrapBoldWords(text);
        observer.observe(current, { childList: true, subtree: true, characterData: true });
    });

    observer.observe(current, { childList: true, subtree: true, characterData: true });
}

function updateVariantDropdownLayout(niceSelectEl: HTMLElement): void {
    const dropdown = niceSelectEl.querySelector<HTMLElement>('.nice-select-dropdown');
    const list = niceSelectEl.querySelector<HTMLElement>('.list');

    if (!dropdown || !list) {
        return;
    }

    dropdown.classList.remove('nice-select-dropdown--wrap');
    dropdown.style.width = '';
    dropdown.style.maxWidth = '';
    dropdown.style.left = '';

    const selectRect = niceSelectEl.getBoundingClientRect();
    const selectWidth = Math.ceil(selectRect.width);
    const maxViewportWidth = Math.max(0, window.innerWidth - DROPDOWN_VIEWPORT_GAP * 2);
    const requiredWidth = Math.ceil(Math.max(selectWidth, list.scrollWidth));
    const dropdownWidth = Math.min(Math.max(selectWidth, requiredWidth), maxViewportWidth);
    const dropdownViewportLeft = Math.max(
        DROPDOWN_VIEWPORT_GAP,
        Math.min(selectRect.left, window.innerWidth - DROPDOWN_VIEWPORT_GAP - dropdownWidth),
    );
    const dropdownLeftOffset = dropdownViewportLeft - selectRect.left;

    if (dropdownLeftOffset !== 0) {
        dropdown.style.left = `${dropdownLeftOffset}px`;
    }

    dropdown.style.width = `${dropdownWidth}px`;
    dropdown.style.maxWidth = `${dropdownWidth}px`;

    if (requiredWidth > dropdownWidth) {
        dropdown.classList.add('nice-select-dropdown--wrap');
    }
}

function bindResponsiveVariantDropdown(select: HTMLSelectElement, niceSelectEl: HTMLElement): void {
    if (!select.closest('.package-item__select--detail')) {
        return;
    }

    let resizeFrame: number | null = null;

    select.addEventListener('modalopen', () => {
        requestAnimationFrame(() => updateVariantDropdownLayout(niceSelectEl));
    });

    window.addEventListener('resize', () => {
        if (niceSelectEl.classList.contains('open')) {
            if (resizeFrame !== null) {
                window.cancelAnimationFrame(resizeFrame);
            }

            resizeFrame = window.requestAnimationFrame(() => {
                updateVariantDropdownLayout(niceSelectEl);
                resizeFrame = null;
            });
        }
    });
}

export default class NiceSelect {
    private static initialized = false;

    static init(): void {
        if (this.initialized) {
            return;
        }

        this.initializeSelects();
        this.initialized = true;
    }

    private static initializeSelects(): void {
        const selectElements = document.querySelectorAll<HTMLSelectElement>('.form-select');

        if (selectElements.length > 0) {
            selectElements.forEach((select) => {
                this.bindToSelect(select);
            });
        }
    }

    private static bindToSelect(select: HTMLSelectElement): void {
        try {
            if (typeof window.NiceSelect !== 'undefined') {
                window.NiceSelect.bind(select);
                const niceSelectEl = select.nextElementSibling;
                if (niceSelectEl && niceSelectEl.classList.contains('nice-select')) {
                    applyBoldToNiceSelect(niceSelectEl);
                    observeCurrentSpan(niceSelectEl);
                    bindResponsiveVariantDropdown(select, niceSelectEl as HTMLElement);
                }
            } else {
                console.warn('NiceSelect library not found');
            }
        } catch (error) {
            console.warn('Failed to bind NiceSelect to element:', error);
        }
    }

    static reinitialize(): void {
        this.initialized = false;
        this.init();
    }

    static bindElement(select: HTMLSelectElement): void {
        this.bindToSelect(select);
    }
}

// Extend the global Window interface to include NiceSelect
declare global {
    interface Window {
        NiceSelect?: {
            bind(element: HTMLSelectElement): void;
        };
    }
}
